// src/Services/chatService.js
import { doc, updateDoc, arrayUnion, getDoc, runTransaction } from "firebase/firestore";
import { db } from "../firebase";

class ChatService {
  constructor() {
    // Message limits per phase
    this.MESSAGE_LIMITS = {
      lobby: 25,
      review: 15,
      results: 15
    };
    
    this.CHARACTER_LIMIT = 250;
  }

  // Generate unique message ID
  generateMessageId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Add a new chat message
  async addMessage(roomDocId, phase, messageData) {
    if (!roomDocId || !phase || !messageData) {
      throw new Error("Missing required parameters for addMessage");
    }

    // Validate message content
    if (!messageData.content || messageData.content.trim().length === 0) {
      throw new Error("Message content cannot be empty");
    }

    if (messageData.content.length > this.CHARACTER_LIMIT) {
      throw new Error(`Message too long. Maximum ${this.CHARACTER_LIMIT} characters.`);
    }

    // Validate required fields to prevent undefined values
    if (!messageData.userId || !messageData.playerName) {
      throw new Error("Missing userId or playerName");
    }

    try {
      // Create message object with explicit validation
      const message = {
        id: this.generateMessageId(),
        userId: String(messageData.userId), // Ensure string
        playerName: String(messageData.playerName), // Ensure string
        content: String(messageData.content.trim()), // Ensure string
        timestamp: new Date(),
        type: messageData.type || "chat" // "chat" or "system"
      };

      if (messageData.subtype) {
        message.subtype = messageData.subtype;
      }

      if (messageData.metadata) {
        message.metadata = messageData.metadata;
      }

      // System messages (game_ended, forced_advance, ...) carry a
      // discriminator the UI switches on — must survive the rebuild above
      // like subtype/metadata do, or every persisted system message reads
      // back indistinguishable from a generic one.
      if (messageData.systemType) {
        message.systemType = messageData.systemType;
      }

      const roomRef = doc(db, "rooms", roomDocId);

      // A plain getDoc()-then-updateDoc() here reads the whole phase array,
      // appends locally, and writes the whole array back. Two callers doing
      // that concurrently (a player's chat message racing a system event
      // like a forced-advance or game-ended notice) can both read the same
      // array and each write back a version missing the other's message —
      // whichever update lands second silently erases the first. A
      // transaction closes that: if the room document changes between this
      // transaction's read and its commit, Firestore retries with a fresh
      // read instead of blindly overwriting.
      await runTransaction(db, async (transaction) => {
        const roomDoc = await transaction.get(roomRef);

        if (!roomDoc.exists()) {
          throw new Error("Room not found");
        }

        const roomData = roomDoc.data();
        const chatData = roomData.chat || {};
        const currentMessages = chatData[phase] || [];

        let updatedMessages = [...currentMessages, message];

        const limit = this.MESSAGE_LIMITS[phase];
        if (updatedMessages.length > limit) {
          updatedMessages = updatedMessages.slice(-limit);
        }

        const updateData = {
          [`chat.${phase}`]: updatedMessages
        };

        if (message.type === "chat") {
          updateData.lastChatUpdate = message.timestamp;
          updateData.lastChatMessage = {
            ...message,
            phase
          };
        }

        // If this is the first chat message ever, initialize the entire chat structure
        if (!roomData.chat) {
          updateData.chat = {
            lobby: phase === "lobby" ? updatedMessages : [],
            review: phase === "review" ? updatedMessages : [],
            results: phase === "results" ? updatedMessages : []
          };
        }

        transaction.update(roomRef, updateData);
      });

      return message;
    } catch (error) {
      console.error("Error adding message:", error);
      throw error;
    }
  }

  // Add a system message (game events, notifications, etc.)
  async addSystemMessage(roomDocId, phase, content) {
    const systemMessage = {
      userId: "system",
      playerName: "NameIt!",
      content,
      type: "system"
    };

    return this.addMessage(roomDocId, phase, systemMessage);
  }

  // Get messages for a specific phase
  getPhaseMessages(roomData, phase) {
    if (!roomData?.chat?.[phase]) {
      return [];
    }

    return roomData.chat[phase].sort((a, b) => {
      // Sort by timestamp (oldest first)
      const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return timeA - timeB;
    });
  }

  // Get all messages across all phases (for debugging or admin views)
  getAllMessages(roomData) {
    const allMessages = [];
    
    ["lobby", "review", "results"].forEach(phase => {
      const phaseMessages = this.getPhaseMessages(roomData, phase);
      allMessages.push(...phaseMessages.map(msg => ({ ...msg, phase })));
    });

    return allMessages.sort((a, b) => {
      const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return timeA - timeB;
    });
  }

  // Validate message before sending
  validateMessage(content) {
    const errors = [];

    if (!content || content.trim().length === 0) {
      errors.push("Message cannot be empty");
    }

    if (content.length > this.CHARACTER_LIMIT) {
      errors.push(`Message too long. Maximum ${this.CHARACTER_LIMIT} characters.`);
    }

    // Basic content filtering (you can expand this)
    const forbiddenWords = ["spam", "test123", "dick", "ball", "fuck"]; // Add actual filtering logic
    const lowerContent = content.toLowerCase();
    
    forbiddenWords.forEach(word => {
      if (lowerContent.includes(word)) {
        errors.push("Message contains inappropriate content");
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

getCurrentChatPhase(roomData) {
  if (!roomData) return "lobby";

  const gameStatus = roomData.gameStatus;

  // Simple mapping based on your 3 states:
  if (gameStatus === "waiting") {
    return "lobby";
  }
  
  if (gameStatus === "starting") {
    // During starting phase, check if we're in review
    const currentRound = roomData.currentRound || 1;
    const currentRoundData = roomData.rounds?.[currentRound];
    
    // If review is completed, show results chat
    if (currentRoundData?.reviewCompleted) {
      return "results";
    }
    
    // If players have submitted but review not completed, show review chat
    const playerCount = roomData.players?.length || 0;
    const submissionCount = Object.keys(currentRoundData?.submissions || {}).length;
    
    if (submissionCount >= playerCount || currentRoundData?.timeExpired) {
      return "review";
    }
    
    // Default to lobby during active gameplay
    return "lobby";
  }
  
  if (gameStatus === "ended") {
    return "results";
  }

  // Default fallback
  return "lobby";
}

// Simplified - chat is always visible
shouldShowChat(roomData) {
  return true; // Always show chat
}

// Simplified phase info
getCurrentPhaseInfo(roomData) {
  const phase = this.getCurrentChatPhase(roomData);
  
  const phaseInfo = {
    lobby: {
      name: "Lobby Chat",
      description: "Chat with other players",
      icon: "💬"
    },
    review: {
      name: "Review Discussion", 
      description: "Discuss answers and vote",
      icon: "🔍"
    },
    results: {
      name: "Results Chat",
      description: "Chat about the results",
      icon: "🎉"
    }
  };
  
  return {
    phase,
    isVisible: true, // Always visible
    ...phaseInfo[phase]
  };
}
}

// Export singleton instance
export const chatService = new ChatService();
